import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IAnnouncement, defaultValue } from 'app/shared/model/announcement.model';

const initialState: EntityState<IAnnouncement> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/announcements';

// Actions

export const getEntities = createAsyncThunk('announcement/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IAnnouncement[]>(requestUrl);
});
export const getEntityannouncement = createAsyncThunk(
  'announcement/fetch_entity_announcement',
  async ({ id, page, size, sort }: { id: string | number; page: number; size: number; sort: string }) => {
    const requestUrl = `${apiUrl}/?userId.equals=${id}&page=${page}&size=${size}&sort=${sort}`;
    return axios.get<IAnnouncement>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);
export const getEntity = createAsyncThunk(
  'announcement/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IAnnouncement>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'announcement/create_entity',
  async (entity: IAnnouncement, thunkAPI) => {
    const result = await axios.post<IAnnouncement>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'announcement/update_entity',
  async (entity: IAnnouncement, thunkAPI) => {
    const result = await axios.put<IAnnouncement>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'announcement/partial_update_entity',
  async (entity: IAnnouncement, thunkAPI) => {
    const result = await axios.patch<IAnnouncement>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'announcement/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IAnnouncement>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const AnnouncementSlice = createEntitySlice({
  name: 'announcement',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addCase(getEntityannouncement.fulfilled, (state, action) => {
        console.log('API Response:', action.payload);
        const { data, headers } = action.payload;

        state.loading = false;
        state.entities = Array.isArray(data) ? data : [data];
        state.totalItems = parseInt(headers['x-total-count'], 10);
        console.log('Updated State:', state);
      })
      // .addCase(getEntityannouncement.fulfilled, (state, action) => {
      //   console.log('API Response:', action.payload);
      //   const { data, headers } = action.payload;

      //   state.loading = false;
      //   state.entities = data;
      //   state.totalItems = parseInt(headers['x-total-count'], 10);
      //   console.log('Updated State:', state);
      // })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })

      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getEntityannouncement), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = AnnouncementSlice.actions;

// Reducer
export default AnnouncementSlice.reducer;
